import { useCallback, useRef } from 'react';
import { JSONContent } from '@tiptap/core';

import Richtext from 'approot/shared/richtext/richtext';
import { useRichtext } from 'approot/shared/richtext/richtext.hooks';
import ActivityBuilderRichtextToolbar from 'approot/activity-builder/activity-builder-richtext-toolbar';
import {
  EditProps,
  SUB_ACTIVITY_INSTRUCTION_SETTINGS,
} from './sub-activity.constants';
import { formatSubActivityLabel } from './sub-activity.utils';
import * as Styled from './sub-activity-edit.style';
import { useInputFieldState } from '../component.hooks';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';

export const SubActivityEdit = ({
  id,
  children,
  subActivityDetails,
  component: { sectionId, __typename },
}: EditProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const onInstructionsChange = useCallback(
    (instructions: JSONContent) => {
      if (subActivityDetails) {
        updateComponentProperties({
          input: {
            sectionId,
            componentId: id,
            componentData: {},
            subActivityDetails: {
              areInstructionsVisible: subActivityDetails.areInstructionsVisible,
              subActivitySort: subActivityDetails.subActivitySort,
              instructions,
            },
          },
          typename: __typename,
        });
      }
    },
    [id, sectionId, subActivityDetails, __typename, updateComponentProperties]
  );

  const { setEditorStateToUpdate } = useInputFieldState<JSONContent>({
    onChange: onInstructionsChange,
  });

  const { editor, toolbarRef, showToolbar } = useRichtext({
    initialValue: subActivityDetails?.instructions,
    onChange: setEditorStateToUpdate,
    settings: SUB_ACTIVITY_INSTRUCTION_SETTINGS,
  });

  return (
    <>
      {(subActivityDetails?.areInstructionsVisible ||
        subActivityDetails?.subActivityLabel) && (
        <Styled.SubActivity ref={containerRef}>
          <Styled.SubActivityLabelWrapper>
            {subActivityDetails?.subActivityLabel && (
              <Styled.SubActivityLabel>
                {formatSubActivityLabel(subActivityDetails?.subActivityLabel)}
              </Styled.SubActivityLabel>
            )}
            <ActivityBuilderRichtextToolbar
              editor={editor}
              toolbarRef={toolbarRef}
              showToolbar={showToolbar}
              canChangeBlockType={false}
            />
            {editor && subActivityDetails?.areInstructionsVisible && (
              <Styled.SubActivityInstruction>
                <Richtext isSmall editor={editor} toolbarRef={toolbarRef} />
              </Styled.SubActivityInstruction>
            )}
          </Styled.SubActivityLabelWrapper>
        </Styled.SubActivity>
      )}
      {children(containerRef.current)}
    </>
  );
};
